import ChapterScene from "@/components/landing/ChapterScene";
import ManifestConceptScene from "@/components/landing/ManifestConceptScene";

const CHAPTERS = [
  {
    nr: "01",
    scene: "torus",
    title: "I build my own programs from the ground up.",
    body: "Most of my projects begin when I want a tool that does something exactly the way I imagine it. Instead of adapting someone else's software, I build my own programs for downloading and converting media, automation, debugging, and everyday workflows. They are personal Raava projects — shaped by my ideas, my use cases, and the features I genuinely want to have.",
    accent: "#FF3B55",
    accentRgb: "255, 59, 85",
    stroke: "rgba(255, 59, 85, 0.6)",
    numberGradient: "linear-gradient(180deg, #FF4A62 0%, rgba(255, 59, 85, 0.08) 88%)",
  },
  {
    nr: "02",
    scene: "pulse",
    title: "The PlayStation 3 is where I go deeper.",
    body: "I develop PS3 mod menus, RTM tools, memory viewers, debuggers, and the utilities that support them. This work combines low-level research, real-time control, interface design, and a lot of testing on real hardware. My goal is to turn complex functionality into tools that are reliable, understandable, and genuinely enjoyable to work with.",
    accent: "#00E5FF",
    accentRgb: "0, 229, 255",
    stroke: "rgba(0, 229, 255, 0.58)",
    numberGradient: "linear-gradient(180deg, #42EEFF 0%, rgba(0, 229, 255, 0.07) 88%)",
  },
  {
    nr: "03",
    scene: "orbit",
    title: "Raava is becoming more than a project name.",
    body: "I am bringing my programs, launchers, website, account system, licensing, downloads, updates, and future ideas into one connected ecosystem. Everything belongs to my own projects and should work well on its own while still feeling like part of the same family. The long-term goal is a platform I can keep expanding whenever a new idea turns into something real.",
    accent: "#A78BFA",
    accentRgb: "167, 139, 250",
    stroke: "rgba(167, 139, 250, 0.6)",
    numberGradient: "linear-gradient(180deg, #B9A5FF 0%, rgba(167, 139, 250, 0.08) 88%)",
  },
];

export default function Manifesto() {
  return (
    <section id="manifest" className="relative px-6 sm:px-12 lg:px-24 py-32 lg:py-48" data-testid="manifesto-section">
      <p className="text-xs uppercase tracking-[0.4em] text-[#00F0FF] mb-20" data-testid="manifesto-eyebrow">
        // Projects &amp; Direction
      </p>
      <div className="space-y-32">
        {CHAPTERS.map((ch, i) => (
          <article
            key={ch.nr}
            className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-start ${i % 2 === 1 ? "lg:text-right" : ""}`}
            data-testid={`manifesto-chapter-${ch.nr}`}
          >
              <div className={`lg:col-span-4 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <span
                  className="font-display text-8xl lg:text-[10rem] leading-none select-none block"
                  style={{
                    fontWeight: 900,
                    backgroundImage: ch.numberGradient,
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    WebkitTextStroke: `1px ${ch.stroke}`,
                    filter: `drop-shadow(0 0 22px ${ch.stroke})`,
                  }}
                >
                  {ch.nr}
                </span>
                <div className="h-52 w-full sm:h-60">
                  {i === 0 ? (
                    <ManifestConceptScene concept="fusion" />
                  ) : i === 1 ? (
                    <ManifestConceptScene concept="reactive" />
                  ) : (
                    <ChapterScene variant={ch.scene} testId={`manifesto-scene-${ch.nr}`} />
                  )}
                </div>
              </div>
              <div
                className={`manifest-copy-panel raava-glow-panel relative w-fit max-w-full rounded-2xl px-5 py-6 sm:p-7 lg:col-span-8 lg:max-w-[48rem] lg:p-9 ${i % 2 === 1 ? "lg:order-1 lg:ml-auto" : "lg:mr-auto"}`}
                style={{ "--panel-accent-rgb": ch.accentRgb }}
              >
                <h2
                  className="manifest-title-gradient font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight"
                  style={{
                    fontWeight: 700,
                    "--manifest-accent": ch.accent,
                    "--manifest-angle": i % 2 === 1 ? "270deg" : "90deg",
                  }}
                >
                  {ch.title}
                </h2>
                <p className={`mt-7 max-w-2xl text-base sm:text-lg text-[#D1D5DE] leading-[1.8] ${i % 2 === 1 ? "lg:ml-auto" : ""}`}>
                  {ch.body}
                </p>
                <div
                  className={`mt-9 h-px w-28 ${i % 2 === 1 ? "lg:ml-auto" : ""}`}
                  style={{ backgroundImage: `linear-gradient(${i % 2 === 1 ? "270deg" : "90deg"}, ${ch.accent}, transparent)` }}
                />
              </div>
          </article>
        ))}
      </div>
    </section>
  );
}
